import React, { useState, FormEvent } from "react";
import ButtonLoader from "../../../../UI/Loaders/Loaders";
import {
  handleSwalSuccessAlert,
  handleSwalErrorAlert,
} from "../../../../helpers";
import { errorHandler } from "../../../../helpers/errorHandler";
import { postData } from "../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";

interface ISendInvestmentStatementModalProps {
  dataRecord: {
    investmentId: number;
    accountBalance: number;
    ledgerBalance: number;
    currency: string;
    customerEmail?: string;
  };
  onCloseModal: () => void;
}

const SendInvestmentStatementModal = ({
  dataRecord,
  onCloseModal,
}: ISendInvestmentStatementModalProps) => {
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    format: "pdf",
    sendToThirdParty: false,
    destinationEmail: dataRecord.customerEmail || "",
  });
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(null);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      sendToThirdParty: checked,
      destinationEmail: checked ? "" : dataRecord.customerEmail || "",
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.startDate) {
      setError("Please select a start date");
      return false;
    }
    if (!formData.endDate) {
      setError("Please select an end date");
      return false;
    }
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setError("Start date cannot be after end date");
      return false;
    }
    if (!formData.destinationEmail) {
      setError("Please provide a destination email");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.destinationEmail)) {
      setError("Please provide a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const fileTypeMap: { [key: string]: string } = {
        pdf: "PDF",
        excel: "Excel",
      };

      const requestBody = {
        investmentId: dataRecord.investmentId,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        fileType: fileTypeMap[formData.format] || "PDF",
        email: formData.destinationEmail,
      };

      const response = await postData(
        ajaxEndpoints.SEND_INVESTMENT_STATEMENT,
        requestBody,
      );

      handleSwalSuccessAlert(
        "Success",
        response?.data?.message || "Investment statement sent successfully",
      );
      onCloseModal();
    } catch (error) {
      const errorMessage = errorHandler(error);
      setError(errorMessage);
      handleSwalErrorAlert(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div
      className="Modal__dialog md-modal animated slideInDown"
      role="document"
    >
      <div className="Modal__content" style={{ overflow: "hidden" }}>
        <div className="Modal__body">
          <React.Fragment>
            <h3 className="Modal__title p-b-1">Send Investment Statement</h3>
            <p className="text-muted mb-4">
              Select the date range and recipient email to send the investment
              statement
            </p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="label-txt form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control user__form"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    max={today}
                    disabled={isSending}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="label-txt form-label">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control user__form"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    max={today}
                    disabled={isSending}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="label-txt form-label">
                    Format <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control user__form"
                    name="format"
                    value={formData.format}
                    onChange={handleInputChange}
                    disabled={isSending}
                  >
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="">
                    <label htmlFor="sendToThirdParty" className="mt-2">
                      Send to third party email address
                    </label>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="sendToThirdParty"
                        name="sendToThirdParty"
                        checked={formData.sendToThirdParty}
                        onChange={handleSwitchChange}
                        disabled={isSending}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="sendToThirdParty"
                      ></label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="label-txt form-label">
                    Recipient Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control user__form"
                    name="destinationEmail"
                    value={formData.destinationEmail}
                    onChange={handleInputChange}
                    placeholder={
                      formData.sendToThirdParty
                        ? "Enter third party email address"
                        : dataRecord.customerEmail
                          ? "Investor email"
                          : "Enter investor email address"
                    }
                    disabled={
                      isSending ||
                      (!formData.sendToThirdParty &&
                        Boolean(dataRecord.customerEmail))
                    }
                    required
                  />
                  <small className="form-text text-muted">
                    {formData.sendToThirdParty
                      ? "Enter the email address where the statement should be sent"
                      : dataRecord.customerEmail
                        ? "Statement will be sent to investor's registered email"
                        : "Enter the investor's email address to send the statement"}
                  </small>
                </div>
              </div>

              <div className="Form__action-buttons m-t-2">
                <button
                  type="button"
                  onClick={onCloseModal}
                  className="btn zoom-element btn login-btn m-0 mr-auto"
                  disabled={isSending}
                >
                  <i className="fa fa-times-circle" /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn zoom-element btn login-btn m-0 ml-auto"
                  disabled={isSending}
                >
                  {isSending ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      <i className="fa fa-paper-plane" /> Send Statement
                    </>
                  )}
                </button>
              </div>
            </form>
          </React.Fragment>
        </div>
      </div>
    </div>
  );
};

export default SendInvestmentStatementModal;
